import React, { useState, Fragment } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { X, Lock, User, Eye, EyeOff, Loader2, CheckCircle, AlertCircle, UserCircle } from 'lucide-react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import { toast } from 'react-toastify';

interface AccountSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: string;
}

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ');
}

const AccountSettingsModal: React.FC<AccountSettingsModalProps> = ({ isOpen, onClose, userId }) => {
    const { user, updateUser } = useAuth();

    // Password state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);

    // Username state
    const [newUsername, setNewUsername] = useState(user?.username || '');
    const [userPassword, setUserPassword] = useState('');
    const [showUserPass, setShowUserPass] = useState(false);

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage(null);

        if (user?.role === 'viewer') {
            setMessage({ type: 'error', text: 'Demo Mode: Write operations are restricted.' });
            return;
        }

        if (newPassword !== confirmPassword) {
            setMessage({ type: 'error', text: 'New passwords do not match' });
            return;
        }

        if (newPassword.length < 6) {
            setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
            return;
        }

        setLoading(true);
        try {
            const result = await api.changePassword(userId, currentPassword, newPassword);
            if (result.success) {
                toast.success('Password changed successfully!');
                setMessage({ type: 'success', text: 'Password changed successfully!' });
                // Reset fields
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setTimeout(() => setMessage(null), 5000);
            } else {
                toast.error(result.message || 'Failed to change password');
                setMessage({ type: 'error', text: result.message || 'Failed to change password' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err instanceof Error ? err.message : 'An error occurred' });
        } finally {
            setLoading(false);
        }
    };

    const handleUsernameSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage(null);

        if (user?.role === 'viewer') {
            setMessage({ type: 'error', text: 'Demo Mode: Write operations are restricted.' });
            return;
        }

        if (newUsername.trim().toLocaleLowerCase() === user?.username.toLocaleLowerCase()) {
            setMessage({ type: 'error', text: 'New username must be different from current one' });
            return;
        }

        setLoading(true);
        try {
            const result = await api.changeUsername(userId, userPassword, newUsername.trim());
            if (result.success) {
                toast.success('Username updated successfully!');
                setMessage({ type: 'success', text: 'Username updated successfully!' });
                updateUser({ username: newUsername.trim() });
                setUserPassword('');
                setTimeout(() => setMessage(null), 5000);
            } else {
                toast.error(result.message || 'Failed to update username');
                setMessage({ type: 'error', text: result.message || 'Failed to update username' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err instanceof Error ? err.message : 'An error occurred' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-2xl transition-all">
                                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                                    <Dialog.Title as="h3" className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                        <UserCircle size={22} className="text-judiciary-600" />
                                        Account Settings
                                    </Dialog.Title>
                                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg">
                                        <X size={20} />
                                    </button>
                                </div>

                                <div className="p-6">
                                    {message && (
                                        <div className={`mb-4 p-3 rounded-xl text-sm flex items-center gap-2 animate-in slide-in-from-top-2 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
                                            }`}>
                                            {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                                            {message.text}
                                        </div>
                                    )}

                                    <Tab.Group>
                                        <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-6">
                                            <Tab
                                                className={({ selected }) =>
                                                    classNames(
                                                        'w-full rounded-lg py-2.5 text-sm font-semibold leading-5 transition-all outline-none',
                                                        selected
                                                            ? 'bg-white text-judiciary-700 shadow-sm'
                                                            : 'text-gray-500 hover:text-gray-700 hover:bg-white/[0.12]'
                                                    )
                                                }
                                            >
                                                <div className="flex items-center justify-center gap-2">
                                                    <Lock size={16} />
                                                    <span>Password</span>
                                                </div>
                                            </Tab>
                                            <Tab
                                                className={({ selected }) =>
                                                    classNames(
                                                        'w-full rounded-lg py-2.5 text-sm font-semibold leading-5 transition-all outline-none',
                                                        selected
                                                            ? 'bg-white text-judiciary-700 shadow-sm'
                                                            : 'text-gray-500 hover:text-gray-700 hover:bg-white/[0.12]'
                                                    )
                                                }
                                            >
                                                <div className="flex items-center justify-center gap-2">
                                                    <User size={16} />
                                                    <span>Username</span>
                                                </div>
                                            </Tab>
                                        </Tab.List>

                                        <Tab.Panels>
                                            <Tab.Panel className="outline-none">
                                                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                                                    <div className="space-y-1">
                                                        <label className="block text-xs font-bold text-gray-500 uppercase">Current Password</label>
                                                        <div className="relative group">
                                                            <input
                                                                type={showCurrent ? "text" : "password"}
                                                                value={currentPassword}
                                                                onChange={(e) => setCurrentPassword(e.target.value)}
                                                                className="w-full border border-gray-200 rounded-xl pl-3 pr-10 py-3 focus:ring-2 focus:ring-judiciary-500/20 focus:border-judiciary-500 outline-none transition-all text-gray-900 bg-gray-50 group-focus-within:bg-white font-medium"
                                                                placeholder="Enter current password"
                                                                required
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowCurrent(!showCurrent)}
                                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-judiciary-600 transition-colors"
                                                            >
                                                                {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-1">
                                                        <label className="block text-xs font-bold text-gray-500 uppercase">New Password</label>
                                                        <div className="relative group">
                                                            <input
                                                                type={showNew ? "text" : "password"}
                                                                value={newPassword}
                                                                onChange={(e) => setNewPassword(e.target.value)}
                                                                className="w-full border border-gray-200 rounded-xl pl-3 pr-10 py-3 focus:ring-2 focus:ring-judiciary-500/20 focus:border-judiciary-500 outline-none transition-all text-gray-900 bg-gray-50 group-focus-within:bg-white font-medium"
                                                                placeholder="Select a strong password"
                                                                required
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowNew(!showNew)}
                                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-judiciary-600 transition-colors"
                                                            >
                                                                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-1">
                                                        <label className="block text-xs font-bold text-gray-500 uppercase">Confirm New Password</label>
                                                        <input
                                                            type="password"
                                                            value={confirmPassword}
                                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                                            className="w-full border border-gray-200 rounded-xl px-3 py-3 focus:ring-2 focus:ring-judiciary-500/20 focus:border-judiciary-500 outline-none transition-all text-gray-900 bg-gray-50 focus:bg-white font-medium"
                                                            placeholder="Re-type new password"
                                                            required
                                                        />
                                                    </div>

                                                    <div className="pt-2">
                                                        <button
                                                            type="submit"
                                                            disabled={loading}
                                                            className="w-full bg-judiciary-600 text-white rounded-xl py-3.5 font-bold hover:bg-judiciary-700 shadow-lg shadow-judiciary-600/20 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                                        >
                                                            {loading && <Loader2 className="animate-spin" size={18} />}
                                                            Update Password
                                                        </button>
                                                    </div>
                                                </form>
                                            </Tab.Panel>

                                            <Tab.Panel className="outline-none">
                                                <form onSubmit={handleUsernameSubmit} className="space-y-4">
                                                    <div className="space-y-1">
                                                        <label className="block text-xs font-bold text-gray-500 uppercase">New Username</label>
                                                        <div className="relative group">
                                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                                            <input
                                                                type="text"
                                                                value={newUsername}
                                                                onChange={(e) => setNewUsername(e.target.value)}
                                                                className="w-full border border-gray-200 rounded-xl pl-10 pr-3 py-3 focus:ring-2 focus:ring-judiciary-500/20 focus:border-judiciary-500 outline-none transition-all text-gray-900 bg-gray-50 focus:bg-white font-medium"
                                                                placeholder="Enter unique username"
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="space-y-1">
                                                        <label className="block text-xs font-bold text-gray-500 uppercase">Verify Password</label>
                                                        <div className="relative group">
                                                            <input
                                                                type={showUserPass ? "text" : "password"}
                                                                value={userPassword}
                                                                onChange={(e) => setUserPassword(e.target.value)}
                                                                className="w-full border border-gray-200 rounded-xl pl-3 pr-10 py-3 focus:ring-2 focus:ring-judiciary-500/20 focus:border-judiciary-500 outline-none transition-all text-gray-900 bg-gray-50 focus:bg-white font-medium"
                                                                placeholder="Enter current password to confirm"
                                                                required
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowUserPass(!showUserPass)}
                                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-judiciary-600 transition-colors"
                                                            >
                                                                {showUserPass ? <EyeOff size={18} /> : <Eye size={18} />}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="pt-2">
                                                        <button
                                                            type="submit"
                                                            disabled={loading}
                                                            className="w-full bg-judiciary-600 text-white rounded-xl py-3.5 font-bold hover:bg-judiciary-700 shadow-lg shadow-judiciary-600/20 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                                        >
                                                            {loading && <Loader2 className="animate-spin" size={18} />}
                                                            Update Username
                                                        </button>
                                                    </div>
                                                    <p className="text-[10px] text-gray-400 text-center leading-relaxed">
                                                        Changing your username will update your login credentials immediately.
                                                    </p>
                                                </form>
                                            </Tab.Panel>
                                        </Tab.Panels>
                                    </Tab.Group>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default AccountSettingsModal;
